<template>
  <section
    v-if="visible"
    class="orangehrm-calendar-notice"
    aria-label="Google Calendar recommendation"
  >
    <div class="orangehrm-calendar-notice-content">
      <oxd-text tag="p" class="orangehrm-calendar-notice-copy">
        Connect Google Calendar once to add your leave dates automatically.
      </oxd-text>
      <div v-if="dismissError" class="orangehrm-calendar-notice-error">
        <oxd-text tag="p" role="alert">
          We couldn't dismiss this recommendation. Please try again.
        </oxd-text>
        <oxd-button
          type="button"
          display-type="ghost"
          label="Try again"
          :disabled="dismissing"
          @click="dismiss"
        />
      </div>
    </div>
    <a :href="connectUrl" class="orangehrm-calendar-notice-connect">
      Connect Google Calendar
    </a>
    <oxd-icon-button
      class="orangehrm-calendar-notice-dismiss"
      type="button"
      name="x"
      title="Dismiss Google Calendar recommendation"
      aria-label="Dismiss Google Calendar recommendation"
      :disabled="dismissing"
      @click="dismiss"
    />
  </section>
</template>

<script>
import axios from 'axios';
import {onMounted, ref} from 'vue';

const endpoint = '/api/v2/leave/google-calendar-notice';
const headers = {
  Accept: 'application/json',
  'Cache-Control': 'no-store',
};

export default {
  setup() {
    const visible = ref(false);
    const connectUrl = ref('');
    const dismissing = ref(false);
    const dismissError = ref(false);
    // This optional notice must not invoke the app-wide reload/error interceptors
    // or cache another authenticated session's recommendation state.
    const http = axios.create({
      baseURL: window.appGlobal?.baseUrl ?? '',
      timeout: 10000,
    });

    onMounted(async () => {
      try {
        const response = await http.get(endpoint, {headers});
        const notice = response.data?.data;
        const url = notice?.connectUrl;
        if (
          notice?.visible === true &&
          typeof url === 'string' &&
          url.startsWith('/') &&
          !url.startsWith('//')
        ) {
          connectUrl.value = url;
          visible.value = true;
        }
      } catch {
        // Loading a recommendation should never prevent the current page loading.
      }
    });

    const dismiss = async () => {
      if (dismissing.value || !visible.value) return;
      dismissing.value = true;
      dismissError.value = false;
      try {
        const response = await http.put(endpoint, {}, {headers});
        if (response.data?.data?.visible !== false) {
          throw new Error('Dismissal was not confirmed');
        }
        visible.value = false;
      } catch {
        dismissError.value = true;
      } finally {
        dismissing.value = false;
      }
    };

    return {visible, connectUrl, dismissing, dismissError, dismiss};
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-calendar-notice {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 0.75rem;
  background-color: $oxd-white-color;

  &-content {
    flex: 1;
    min-width: 0;
  }

  &-copy {
    line-height: 1.5;
  }

  &-connect {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
    min-height: 44px;
    padding: 8px 32px;
    border-radius: 3em;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    color: $oxd-secondary-font-color;
    background-color: $oxd-secondary-four-color;
    text-align: center;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      background-color: $oxd-secondary-four-lighten-5-color;
    }

    &:focus-visible {
      outline: 2px solid $oxd-secondary-four-color;
      outline-offset: 3px;
    }

    &:active {
      background-color: $oxd-secondary-four-darken-5-color;
    }
  }

  &-error {
    margin-top: 0.5rem;
  }

  &-dismiss {
    flex-shrink: 0;
    align-self: flex-start;
  }

  @media (max-width: 640px) {
    position: relative;
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;

    &-content {
      padding-right: 2rem;
    }

    &-dismiss {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
    }
  }
}
</style>
